import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { askPeriod, formatNumber, periodRangeLabel } from '../analyticsRange.js';
import { DrillPanel, WeeklyPanel, type Drill, TrendFailure } from '../drilldown.js';
import { METRIC_ABOUT, type MetricAboutKey } from '../explain.js';
import {
  DrilldownTable,
  MeasureStat,
  Panel,
  Stat,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { measure, SUPPRESSED_LABEL } from '../disclosure.js';
import { LoadFailure } from '../../../LoadFailure.js';
import { explainDraft } from '../../../intelligence/global/index.js';

type ClientsByProgramRow = DashboardNS.ClientsByProgramRow;

export function ClientsView() {
  const { apiQuery, setView } = useAnalyticsControls();
  const [drill, setDrill] = useState<Drill | null>(null);

  const clients = useQuery({
    queryKey: ['analytics', 'clients', apiQuery],
    queryFn: () => dashboard.analyticsClients(apiQuery),
  });

  // The same period as the figures above it (it used to be a fixed 26 weeks).
  const trends = useQuery({
    queryKey: ['analytics', 'trends', apiQuery],
    queryFn: () => dashboard.analyticsTrends(apiQuery),
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (clients.isError && isForbidden(clients.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (clients.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (clients.isError && !clients.data) {
    return (
      <LoadFailure
        error={clients.error}
        message="We couldn’t load client analytics just now, so read this screen as unknown rather than empty."
        onRetry={() => void clients.refetch()}
      />
    );
  }
  const d = clients.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={4} />;

  const open = (label: string, key: MetricAboutKey) => () =>
    setDrill({
      title: `${label}, ${periodRangeLabel(d.period)}`,
      about: METRIC_ABOUT[key],
      ask: explainDraft(label.toLowerCase(), askPeriod(d.period)),
      records: { set: 'delivered', from: d.period.from, to: d.period.to, scope: d.scope },
    });

  const columns: Column<ClientsByProgramRow>[] = [
    {
      key: 'program',
      header: 'Program',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.programName}</span>,
    },
    { key: 'served', header: 'Served', render: (r) => String(r.clientsServed), numeric: true },
    {
      // PER ROW, BECAUSE THE FLOOR IS PER ROW. A program with three clients
      // has its two measures nulled while its neighbour keeps them, so the
      // table cannot render one rule for the whole grid. `String(null)` here
      // printed the word "null" into the cell.
      key: 'appointments',
      header: 'Services delivered',
      render: (r) => (r.appointments == null ? SUPPRESSED_LABEL : String(r.appointments)),
      numeric: true,
    },
    {
      key: 'hours',
      // Ritten-backed service minutes — never "hours worked" (timekeeping).
      header: 'Service hours delivered',
      render: (r) => (r.hours == null ? SUPPRESSED_LABEL : formatNumber(r.hours, 1)),
      numeric: true,
    },
  ];

  return (
    <div className="an-stack">
      <StatGrid>
        <Stat
          label="Active clients"
          value={String(d.activeClients)}
          sub={`${d.totalClients} total`}
        />
        <Stat
          label="Clients served"
          value={String(d.clientsServed)}
          onOpen={open('Clients served', 'clientsServed')}
        />
        {/*
          THE FOUR FIGURES THAT STAND ON `clientsServed`, EACH THROUGH
          `measure()`. That helper is the one place that tells a SUPPRESSED
          blank from an ABSENT one: `avgFrequency` is legitimately null when no
          client was served at all, and reading that as "hidden" would claim a
          withholding that never happened. The envelope decides, not the null.
        */}
        <MeasureStat
          label="Services delivered"
          display={measure(d.appointments, 'appointments', d.disclosure, String)}
          onOpen={open('Services delivered', 'servicesDelivered')}
        />
        <MeasureStat
          label="Avg visits / client"
          display={measure(d.avgFrequency, 'avgFrequency', d.disclosure, (v) => formatNumber(v, 1))}
        />
        <MeasureStat
          label="Clients with care gaps"
          display={measure(d.gapClients, 'gapClients', d.disclosure, String)}
        />
      </StatGrid>

      {drill ? <DrillPanel key={drill.title} drill={drill} onClose={() => setDrill(null)} /> : null}

      <StalenessStrip staleness={d.staleness} />

      <ClientsTrendPanel trends={trends} onDrill={setDrill} />

      <Panel
        title="By program"
        subtitle={
          <button type="button" className="rn-link" onClick={() => setView('careGaps')}>
            {d.gapClients != null && d.gapClients > 0 ? 'View care gaps →' : 'Care gaps'}
          </button>
        }
      >
        <DrilldownTable
          rows={d.byProgram}
          isLoading={false}
          isError={false}
          onRetry={() => void clients.refetch()}
          columns={columns}
          rowKey={(r) => r.programId ?? r.programName}
          emptyHeading="No service activity"
          emptyMessage="Programs appear once service events are recorded."
        />
      </Panel>
    </div>
  );
}

function ClientsTrendPanel({
  trends,
  onDrill,
}: {
  trends: QueryLike<DashboardNS.AnalyticsTrends>;
  onDrill: (drill: Drill) => void;
}) {
  if (trends.isLoading) return <SkeletonSet surface="band" />;
  if (trends.isError && !trends.data) {
    return (
      <TrendFailure
        error={trends.error}
        message="We couldn’t load client trends just now."
        onRetry={() => void trends.refetch()}
      />
    );
  }
  const t = trends.data;
  if (!t) return null;
  return (
    <div className="an-grid">
      <WeeklyPanel
        title="Clients served"
        axis={t}
        about={METRIC_ABOUT.clientsServed}
        onDrill={onDrill}
        lines={[
          {
            name: 'Clients served',
            color: 'var(--chart-3)',
            series: t.series.clientsServed,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Services delivered"
        axis={t}
        about={METRIC_ABOUT.servicesDelivered}
        additive
        onDrill={onDrill}
        lines={[
          {
            name: 'Services delivered',
            color: 'var(--chart-2)',
            series: t.series.appointments,
            set: 'delivered',
          },
        ]}
      />
    </div>
  );
}
